import { BEIGE_500, BEIGE_600, BLACK_TEXT, CORAL_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { MessageDeliveryDTO, MessageDeliveryStatus } from 'models/messageThreads'
import { Nullable } from 'models/helpers'
import React, { useMemo } from 'react'

import { AttachedFiles } from 'components/common/AttachedFiles'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { BoxProps } from '@mui/material/Box'
import { LoadingWrapper } from 'components/common/LoadingWrapper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

/**
 * Represents the type of a message in a thread.
 * 
 * @property {'sent'} sent - Indicates that the message was sent by the user.
 * @property {'received'} received - Indicates that the message was received by the user.
 */
export type MessageType = 'sent' | 'received'

/** Represents a file attached to a message. */
export type MessageAttachedFile = {
  /** The ID of the attached file. */
  id: string
  /** The name of the attached file. */
  fileName: string
  /** The URL to the attached file. */
  fileUrl: string
}

/** @interface ThreadMessageProps for the ThreadMessage component. */
export interface ThreadMessageProps extends BoxProps {
  /** The type of the displayed message either 'received' or 'sent'. */
  type: MessageType
  /** The text content of the message. */
  message: string
  /** An optional file attached to the message. */
  attachedFiles?: MessageAttachedFile[]
  /** Whether the message is being sent. */
  isSending?: boolean
  /** Whether to display the participant name. */
  showParticipantName?: boolean
  /** Delivery of a sent message to the provider; a notice is shown while some of it is not delivered. */
  delivery?: Nullable<MessageDeliveryDTO>
}

/**
 * Translation key and attachment list of the notice for a message not entirely delivered, or null when delivered.
 * An admin's failed notice leaves out the advice to contact support, as admins are the ones who resend it.
 */
export const deliveryNotice = (
  delivery?: Nullable<MessageDeliveryDTO>,
  forAdmin = false
): { key: string, files: string } | null => {
  if (!delivery || delivery.status === MessageDeliveryStatus.DELIVERED) return null
  const failed = delivery.status === MessageDeliveryStatus.FAILED
  const part = delivery.textPending ? 'message' : 'attachments'
  const audience = failed && forAdmin ? '_admin' : ''
  return {
    key: `delivery.${failed ? 'failed' : 'pending'}_${part}${audience}`,
    files: delivery.pendingAttachmentFilenames.join(', '),
  }
}

/**
 * @component
 * ThreadMessage renders a thread message with optional attached file and styled different according to the viewer.
 *
 * @example
 * <ThreadMessage type='sent' message='Hello, how are you?' attachedFile={fileName: "yes.pdf", fileUrl: 'https://via.placeholder.com/150'} />
 */
export const ThreadMessage: React.FC<ThreadMessageProps> = ({
  message,
  type,
  isSending,
  attachedFiles,
  showParticipantName,
  delivery,
  ...boxProps
}) => {

  const { t } = useTranslation('threads')

  const notice = useMemo(() => deliveryNotice(delivery, !!showParticipantName), [delivery, showParticipantName])

  /** Custom border radius to display sharp border according to the type of displayed message. */
  const borderRadius = useMemo(() => {
    if (type === 'sent') return '0.8rem 0.8rem 0 0.8rem'
    return '0.8rem 0.8rem 0.8rem 0'
  }, [type])

  return (
    <BorderBoxWrapper
      {...boxProps}
      padding={1}
      borderColor={BEIGE_600}
      elevation={type === 'received' ? 'none' : 'xs'}
      backgroundColor={type === 'received' ? BEIGE_500 : WHITE}
      sx={{ borderRadius }}
    >
      <LoadingWrapper isLoading={!!isSending}>

        <Stack gap={0.5}>

          {showParticipantName &&
            // This is visible for admins only and might be used generally for all users in the future
            <Typography variant="text-sm" color={GRAY_900} fontWeight={600}>
              {type === 'sent' ? t('participant_client') : t('participant_provider')}
            </Typography>
          }

          <Typography variant="text-sm" color={BLACK_TEXT} className="fs-mask">
            {message}
          </Typography>

        </Stack>

        {!!attachedFiles?.length && <AttachedFiles attachedFiles={attachedFiles} />}

        {notice &&
          <Typography variant="text-xs" color={CORAL_800} marginTop={0.5} data-testid="thread-message-delivery-notice">
            {t(notice.key, { files: notice.files })}
          </Typography>
        }

      </LoadingWrapper>

    </BorderBoxWrapper>
  )
}
